/* eslint-disable ember/no-runloop */
import { isArray } from '@ember/array';
import { later } from '@ember/runloop';
import { cleanExcelSheetName } from '@linc-technologies/linced-common/utils/file-names';
import { getDynamicStyles } from '@linc-technologies/linced-common/utils/table';
import { isEmpty } from '@ember/utils';

export function getTablePrintCss(landscape) {
  return `
    <link rel="stylesheet" href="${
      window.location.origin
    }/assets/icons/linced-material.css">
    <style type="text/css">
      /* Don't print occluded content placeholder */
      occluded-content {
        display: none;
      }

      body {
        font-family: -apple-system, BlinkMacSystemFont, Inter, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
      }

      h1 {
        text-align: center;
        text-transform: uppercase;
        font-size: 1em;
      }

      table {
        width: 100%;
        font-size: 0.8em;
      }
      table, tr, td, th {
        border: 1px solid black;
        border-collapse: collapse;
      }
      tr {
        page-break-inside: avoid;
        page-break-after:auto
      }
      td, th {
        padding: 2px;
        word-wrap: break-word;
      }
      .hide-print,
      tr.field-filters,
      thead md-icon.md-sort-icon {
        display: none;
      }
      table ul {
        margin-bottom: 0;
      }
      .dob-labels td {
        height: 150px;
        line-height: 2;
        padding: 0 20px;
      }
      .attendance-audit table {
        font-size: 50%;
      }

      .md-button {
        white-space: nowrap;
        text-transform: uppercase;
      }

      md-progress-linear {
        display: block;
        position: relative;
        width: 100%;
        height: 5px;
        padding-top: 0 !important;
        margin-bottom: 0 !important;
      }
      md-progress-linear._md-progress-linear-disabled {
        visibility: hidden;
      }
      md-progress-linear .md-container {
        display: block;
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 5px;
      }
      md-progress-linear .md-container {
        .md-bar {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 100%;
          height: 5px;
        }
        .md-bar1 {
            background-color: var(--gender-color-female);
        }
        .md-bar2 {
            background-color: var(--gender-color-male);
        }
      }

      md-progress-linear .md-container .md-dashed:before {
        content: "";
        display: none;
        position: absolute;
        margin-top: 0;
        height: 5px;
        width: 100%;
        background-color: transparent;
        background-size: 10px 10px !important;
        background-position: 0px -23px;
      }
      md-progress-linear .md-container.md-mode-buffer {
        background-color: transparent !important;
      }
      md-progress-linear .md-container.md-mode-buffer .md-dashed:before {
        display: block;
      }

      .page-break-after,
      .page-break-after > table {
        break-after: page;
      }

      .auto-page-break-after,
      .auto-page-break-after > table {
        break-after: auto;
        break-inside: avoid;
      }

      [class*="--display-none"]{
        display: none;
      }
      .list--medical-room__learner-name {
        display: block;
      }
      .list--medical-room ul {
        padding-left: 0;
        list-style-type: none;
        margin-block-end: 0;
        padding-inline-start: 0;
        margin-block-start: 0;
      }
      .color-red {
        color: red;
      }

      ${landscape ? `@media print{@page {size: landscape}}` : ``}
    </style>
  `;
}

export const tablePrintCss = getTablePrintCss(false);

export { getDynamicStyles };

export function getTableExportData(
  containerId,
  format,
  parseCell,
  bodyData = null,
) {
  let notCheckbox = ':not(.md-checkbox-column):not(.skip-export)';
  let table = `#${containerId} > table > div`;
  // Check if div exists or not
  if (isEmpty(document.querySelectorAll(table))) {
    table = `#${containerId} > table`;
  }
  let headerSelector = `${table} > thead > tr:not(.skip-export)`;
  let headerRows = document.querySelectorAll(headerSelector);
  let header = getExportData(
    headerRows,
    `${headerSelector} > th${notCheckbox}`,
    format,
    parseCell,
  );
  let bodySelector = `${table} > tbody > tr:not(.field-filters)`;
  let bodyRows = document.querySelectorAll(bodySelector);
  let body = isArray(bodyData)
    ? { rows: bodyData, merges: [] }
    : getExportData(
        bodyRows,
        `${bodySelector} > td${notCheckbox}`,
        format,
        parseCell,
      );
  let footerSelector = `${table} > tfoot > tr`;
  let footerRows = document.querySelectorAll(footerSelector);
  let footerCells = `${footerSelector} > th${notCheckbox}, ${footerSelector} > td${notCheckbox}`;
  let footer = getExportData(footerRows, footerCells, format, parseCell);

  // Build a single array of all rows (header + body + footer).
  let data = [];
  data.push(...header.rows);
  data.push(...body.rows);
  data.push(...footer.rows);

  // Build a single array of all merges, offsetting body and footer by the relevant number of rows.
  let offsetMerges = (merge) => {
    merge.s.r += offset;
    merge.e.r += offset;
  };
  let merges = [];
  merges.push(...header.merges);
  let offset = header.rows.length;
  body.merges.forEach(offsetMerges);
  merges.push(...body.merges);
  offset = header.rows.length + body.rows.length;
  footer.merges.forEach(offsetMerges);
  merges.push(...footer.merges);

  return { rows: data, merges };
}

function getExportData(rows, selector, format, parseCell) {
  let output = [];
  let merges = [];

  // Add a row to the output array for each row in the table's header.
  rows.forEach(() => output.push([]));

  let r = 0;
  rows.forEach((row) => {
    let c = 0;
    row.querySelectorAll(selector).forEach((cell) => {
      let rowSpan = +cell.getAttribute('rowspan');
      let colSpan = +cell.getAttribute('colspan') || 1;
      let cellValue = parseCell(cell);

      // Apply merge offsets if necessary
      for (let midx = 0; midx < merges.length; ++midx) {
        let m = merges[midx];
        if (m.s.c === c && m.s.r <= r && r <= m.e.r) {
          c = m.e.c + 1;
          midx = -1;
        }
      }

      if (rowSpan > 0 || colSpan > 1) {
        merges.push({
          s: { r, c },
          e: { r: r + (rowSpan || 1) - 1, c: c + colSpan - 1 },
        });

        if (format === 'xlsx') {
          output[r][c] = cellValue;
        } else {
          // CSV - populate all the merged cells with the same value.
          // A rowspan can go past the last row. Stop at the last row.
          let lastRow = Math.min(r + (rowSpan || 1), output.length);
          for (let outRow = r; outRow < lastRow; outRow++) {
            for (let outCol = c; outCol < c + colSpan; outCol++) {
              output[outRow][outCol] = cellValue;
            }
          }
        }
      } else {
        // No spans, just a single cell.
        output[r][c] = cellValue;
      }
      c += colSpan;
    });
    r++;
  });

  return { rows: output, merges };
}

// parseCell is a default implementation of parsing a table cell to generate plain text for spreadsheet exports.
export function parseCell(cell) {
  // Deep clone the cell, so we don't change the actual cell.
  let clone = cell.cloneNode(true);

  // Replace all title-bearing elements with their titles.
  clone.querySelectorAll('[title]').forEach((el) => {
    let textNode = document.createTextNode(el.title);
    el.parentNode.replaceChild(textNode, el);
  });
  // And any which use data-title to avoid browsers rendering the title as a tooltip.
  clone.querySelectorAll('[data-title]').forEach((el) => {
    let textNode = document.createTextNode(el.dataset.title);
    el.parentNode.replaceChild(textNode, el);
  });

  // Remove sort direction icons
  clone
    .querySelectorAll('.md-sort-icon')
    .forEach((el) => el.parentNode.removeChild(el));

  // Replace filter list icons with an empty value
  clone
    .querySelectorAll('[md-font-icon="filter_list"]')
    .forEach((el) => (el.innerText = ''));

  // Remove clear icons from input fields
  clone
    .querySelectorAll('.ember-power-select-clear-btn')
    .forEach((el) => el.parentNode.removeChild(el));

  // Return the element's innerText, with all excess whitespace removed.
  return clone.innerText.replace(/\s+/g, ' ').trim();
}

export function getMultiTablesExportData(titles, tables) {
  let sheets = [];

  for (let i = 0; i < titles.length; i++) {
    let sheetName = cleanExcelSheetName(titles[i].innerText);
    let data = getTableExportData(tables[i].id, 'xlsx', parseCell);

    sheets.push({
      name: sheetName,
      data: data.rows,
    });
  }

  return sheets;
}

// printMultiTables returns false when a popup blocker stops the print window.
export function printMultiTables(heading, titles, tables) {
  let printer = window.open(
    'about:blank',
    '_blank',
    'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
  );
  if (!printer) {
    return false;
  }

  let title = `<h1>${heading}</h1>`;
  let styles = getTablePrintCss(true) + getDynamicStyles();

  let content = '';

  for (let i = 0; i < titles.length; i++) {
    content += `<div class="auto-page-break-after">
                      ${titles[i].outerHTML}
                      ${tables[i].outerHTML}
                    </div>`;
  }

  printer.document.head.innerHTML = styles;
  printer.document.body.innerHTML = title + content;

  later(
    this,
    (printer) => {
      printer.print();
      printer.close();
    },
    printer,
    500,
  );

  return true;
}
